import { apmCaptureError } from '../apm'
import { type LOCALE } from '../i18n'
import { urlNormalizeUri } from '../url-handling'

import { cmsExcludeEmptyString } from './cmsExcludeEmptyString.ts'
import { cmsStoryblokSlugToPublicUrl } from './cmsStoryblokSlugToPublicUrl'
import { type StoryblokMultilink } from './storyblok'

const getStoryMultilinkHref = (
  multilink: StoryblokMultilink & { linktype: 'story' },
): string | undefined => {
  const { story } = multilink

  const slug = story && 'full_slug' in story && story.full_slug

  if (!slug) {
    return undefined
  }

  return cmsStoryblokSlugToPublicUrl(slug, {
    addTenantHostFromPathPrefix: true,
  })
}

const getUrlMultilinkHref = (
  multilink: StoryblokMultilink & { linktype: 'url' },
  locale?: LOCALE,
): string | undefined => {
  const { url } = multilink

  if (!url) {
    return undefined
  }

  if (url.startsWith('#') || url.startsWith('?')) {
    return url
  }

  try {
    return new URL(url).href
  } catch {
    return urlNormalizeUri({
      locale,
      uri: url,
    })
  }
}

const getEmailMultilinkHref = (
  multilink: StoryblokMultilink & { linktype: 'email' },
): string | undefined => {
  const { email } = multilink

  if (!email) {
    return undefined
  }

  try {
    return new URL(email).href
  } catch {
    return `mailto:${email}`
  }
}

export type CmsStoryblokLinkProps = {
  href: string
  rel?: string
  target?: string
}

/**
 * Convert Storyblok link field payload into React friendly props for an anchor element
 */
const cmsGetStoryblokLinkProps = (
  multilink: StoryblokMultilink,

  /**
   * If provided, the relative link will be processed with the correct language prefix
   */
  locale?: LOCALE,
): CmsStoryblokLinkProps | undefined => {
  let href: string | null | undefined = null

  switch (multilink.linktype) {
    case 'email': {
      href = getEmailMultilinkHref(multilink)

      break
    }

    case 'story': {
      href = getStoryMultilinkHref(multilink)

      break
    }

    case 'url': {
      href = getUrlMultilinkHref(multilink, locale)

      break
    }

    default: {
      apmCaptureError(
        new Error(
          `Link field type "${multilink.linktype}" is not supported yet.`,
        ),
      )

      return undefined
    }
  }

  if (!href) {
    return undefined
  }

  return {
    href: multilink.anchor ? `${href}#${multilink.anchor}` : href,
    rel:
      multilink.rel || multilink.target === '_blank' ? 'noreferrer' : undefined,
    target: cmsExcludeEmptyString(multilink.target),
  }
}

/**
 * Convert Storyblok Link field value into href compatible value based on user input or story information.
 *
 * `linktype` processing:
 * - `email` returns `mailto:` link
 * - `story` returns **absolute** URL based on story slug
 * - `url` returns link based on user input
 */
export const cmsGetStoryblokLinkHref = (
  multilink: StoryblokMultilink,

  /**
   * If provided, the relative link will be processed with the correct language prefix
   */
  locale?: LOCALE,
): string | undefined => cmsGetStoryblokLinkProps(multilink, locale)?.href

/**
 * Handle Web & App links together. Use web link as fallback in WebView when app link is not provided.
 * Common use case is providing properties to links and buttons for Storyblok components.
 */
export const cmsGetStoryblokUniversalLinkProps = (
  {
    appLink,
    webLink,
  }: {
    appLink?: StoryblokMultilink | string
    webLink: StoryblokMultilink
  },
  {
    locale,
    webview,
  }: {
    locale?: LOCALE
    webview: boolean
  },
): CmsStoryblokLinkProps | undefined => {
  let appLinkProps: CmsStoryblokLinkProps | null | undefined = null

  if (webview && appLink) {
    if (typeof appLink === 'string') {
      return {
        href: appLink,
      }
    }

    appLinkProps = cmsGetStoryblokLinkProps(appLink, locale)
  }

  if (appLinkProps) {
    return appLinkProps
  }

  return cmsGetStoryblokLinkProps(webLink, locale)
}
